import React from "react";
import { Calendar, User, ExternalLink } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { sectionLabel, localize, UI, SPECTRALOOM_URL } from "@/lib/constants";
import { HeroBanner } from "@/components/HeroBanner";
import { SocialEmbeds } from "@/components/SocialEmbed";
import { ArticleBody } from "@/components/ArticleBody";
import { ShareButtons } from "@/components/ShareButtons";

// Shared visual renderer used by the public preview page and the in-editor live preview.
export const ArticleView = ({ item, brand, caseLabel, lang = "IT", onLangChange, mode = "preview", feedbackSlot = null }) => {
  const t = UI[lang] || UI.IT;
  const isDefault = brand?.is_default ?? true;
  const brandName = brand?.name || "Spectraloom";
  const logoSrc = brand?.logo_url ? mediaUrl(brand.logo_url) : "/spectraloom-logo.jpg";

  const cover = mediaUrl(item.cover_image_url);
  const title = localize(item.title, lang);
  const body = localize(item.body, lang);
  const dateStr = item.published_at || item.created_at || new Date().toISOString();
  const hasIt = !!(item.title?.it || item.body?.it);
  const hasEn = !!(item.title?.en || item.body?.en);
  const bothLangs = hasIt && hasEn;
  const catLabel = item.category === "case_studies" && caseLabel ? caseLabel : sectionLabel(item.category, lang);
  const dateFmt = (d) => new Date(d).toLocaleDateString(lang === "IT" ? "it-IT" : "en-US", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-[#0B0D12]" data-testid="article-view">
      <header className="sticky top-0 z-40 border-b border-[#2D3748] bg-[#0B0D12]/85 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logoSrc} alt={brandName} className="h-10 w-10 rounded-lg object-contain bg-[#0B0D12]" onError={(e) => { e.currentTarget.src = "/spectraloom-logo.jpg"; }} />
            <span className="font-heading text-lg font-bold tracking-tight text-white">{brandName}{isDefault ? " Blog" : ""}</span>
          </div>
          {bothLangs && (
            <div data-testid="preview-lang-selector" className="flex items-center rounded-full border border-[#06B6D4]/30 bg-[#06B6D4]/5 p-1">
              {["IT", "EN"].map((l) => (
                <button
                  key={l}
                  data-testid={`preview-lang-${l}`}
                  onClick={() => onLangChange && onLangChange(l)}
                  className={`px-3 py-1 rounded-full text-xs font-mono font-semibold transition-colors ${lang === l ? "bg-[#06B6D4] text-[#0B0D12]" : "text-[#06B6D4] hover:bg-[#06B6D4]/10"}`}
                >
                  {l}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6">
        {isDefault && <HeroBanner lang={lang} />}

        <article data-testid="preview-article" className={`max-w-3xl mx-auto ${isDefault ? "py-6" : "py-10"}`}>
          <div className="flex flex-wrap items-center gap-2.5 mb-5">
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#8B5CF6]/15 text-[#a78bfa] border border-[#8B5CF6]/30">
              {catLabel}
            </span>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-[1.08]">
            {title || (lang === "IT" ? "Titolo mancante" : "Missing title")}
          </h1>

          <div className="flex items-center gap-5 mt-5 text-sm text-[#64748B]">
            <span className="flex items-center gap-1.5"><User className="w-4 h-4" /> {item.author || `${brandName} Staff`}</span>
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {dateFmt(dateStr)}</span>
          </div>

          {item.slug && (
            <div className="mt-6"><ShareButtons slug={item.slug} title={title} lang={lang} /></div>
          )}

          {cover && (
            <div className="mt-8 rounded-2xl overflow-hidden border border-[#2D3748]">
              <img src={cover} alt={item.cover_alt || title} className="w-full object-cover" />
            </div>
          )}

          <SocialEmbeds links={item.social_links} />

          {body ? (
            <ArticleBody html={body} testId="preview-body" className="prose-sl mt-8" />
          ) : (
            <p className="mt-8 text-[#64748B] italic">{lang === "IT" ? "Nessun contenuto in questa lingua." : "No content in this language."}</p>
          )}

          <div className="mt-12 pt-6 border-t border-[#2D3748] flex items-center gap-3">
            <img src={item.author_photo_url ? mediaUrl(item.author_photo_url) : logoSrc} alt={item.author_name || `${brandName} Staff`} className="w-11 h-11 rounded-full object-cover shrink-0" onError={(e) => { e.currentTarget.src = "/spectraloom-logo-round.jpg"; }} />
            <div>
              <p className="font-heading text-sm font-semibold text-white leading-tight">
                {item.author_name || (isDefault ? "Spectraloom Staff" : `${brandName} Staff`)}
              </p>
              {item.author_name && item.author_title && <p className="text-xs text-[#a78bfa]">{item.author_title}</p>}
              <p className="text-xs text-[#64748B]">{lang === "IT" ? "Pubblicato il" : "Published on"} {dateFmt(dateStr)}</p>
            </div>
          </div>

          {feedbackSlot}
          <p data-testid="article-ai-disclosure" className="mt-6 italic" style={{ fontSize: "0.8em", opacity: 0.45, color: "#64748B" }}>{t.aiDisclosure}</p>
        </article>
      </main>

      <footer className="mt-24 border-t border-[#2D3748] bg-[#0B0D12]">
        <div className="max-w-6xl mx-auto px-6 py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div className="flex items-center gap-3">
              <img src={logoSrc} alt={brandName} className="h-10 w-10 rounded-lg object-contain bg-[#0B0D12]" onError={(e) => { e.currentTarget.src = "/spectraloom-logo.jpg"; }} />
              <span className="font-heading text-xl font-bold tracking-tight text-white">{brandName}</span>
            </div>
            {isDefault && <p className="text-[#94A3B8] text-sm mt-3 max-w-sm">{t.footerTagline}</p>}
          </div>
          {isDefault && (
            <div className="flex flex-col items-start md:items-end gap-3">
              <a href={SPECTRALOOM_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-medium text-[#8B5CF6] hover:text-[#a78bfa] transition-colors">
                {t.visit} <ExternalLink className="w-4 h-4" />
              </a>
              <p className="text-xs text-[#64748B]">© {new Date().getFullYear()} {brandName}. All rights reserved.</p>
            </div>
          )}
        </div>
      </footer>
    </div>
  );
};

export default ArticleView;
